import { CellContext } from '@tanstack/react-table';
import { Deployment } from 'interfaces/deployment';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import ReactTooltip from 'react-tooltip';
import { ReactQueryKeys } from 'constants/react-query-keys';
import DeploymentService from 'services/DeploymentService';

/**
 * @description Component for displaying deployment type (full or partial) with tooltip showing device information for partial deployments.
 *
 * @SADtrace SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {Object} props - Component props
 * @param {CellContext<Deployment, unknown>} props.info - Cell context containing the deployment data
 *
 * @returns {JSX.Element} Rendered component
 */
function TypeDeploymentColumn({
  info,
}: {
  info: CellContext<Deployment, unknown>;
}) {
  const { t } = useTranslation();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const deployment = info.row.original;

  const {
    data: singleDeploymentData,
    isLoading: loadingSingleDeployment,
    isError,
  } = useQuery({
    queryKey: [ReactQueryKeys.SINGLE_DEPLOYMENT, deployment.id],
    queryFn: () => DeploymentService.getSingleDeployment(deployment.id),
    enabled: isTooltipOpen,
  });

  /**
   * @description Renders the content for the tooltip based on the deployment data and loading state.
   *
   * @SADtrace SAD-003-6.1
   *
   * @History
   * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
   * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
   *
   * @returns {JSX.Element|string} The tooltip content to display
   */
  const renderTooltipContent = () => {
    if (loadingSingleDeployment) {
      return (
        <span
          data-testid={`loading_tooltip_deployment_id_${deployment.id}`}
          className="spinner-border spinner-border-sm me-2"
          role="status"
          aria-hidden="true"
        ></span>
      );
    }
    if (isError) {
      return '';
    }

    if (!singleDeploymentData?.data.deploymentDevices?.length) {
      return (
        <div data-testid={`tooltip_nodevices_id_${deployment.id}`}>
          {t('deployment.partial_deployment.no_devices')}
        </div>
      );
    }

    return (
      <div data-testid={`tooltip_typedeployment_id_${deployment.id}`}>
        {singleDeploymentData.data.deploymentDevices.map((dev) => (
          <div className="py-2" key={dev.id}>
            {dev.device_number}
          </div>
        ))}
      </div>
    );
  };

  /**
   * @description Display error toast when API request fails
   */
  useEffect(() => {
    if (isError) {
      toast.error(
        (isError as any)?.response?.data?.message || t('errors.default'),
      );
    }
  }, [isError, t]);

  return (
    <div
      onMouseEnter={() => {
        if (!deployment.partial_deployment) return;
        setIsTooltipOpen(true);
      }}
      onMouseLeave={() => {
        if (!deployment.partial_deployment) return;
        setIsTooltipOpen(false);
      }}
      className={
        'p-2 text-center' + (deployment.partial_deployment ? '' : ' fw-bold')
      }
      style={{
        minWidth: '150px',
      }}
      data-tip=""
      data-for={`id_${deployment.id}`}
      data-testid={`deployment_type_column_id_${deployment.id}`}
    >
      {t(
        `deployment.table.${
          deployment.partial_deployment
            ? 'partial_deployment'
            : 'full_deployment'
        }`,
      )}
      <ReactTooltip
        id={`id_${deployment.id}`}
        effect="solid"
        place="bottom"
        className="toolTip"
        getContent={() =>
          deployment.partial_deployment && isTooltipOpen
            ? renderTooltipContent()
            : ''
        }
      />
    </div>
  );
}

export default TypeDeploymentColumn;
